/**
 * AR Email Intelligence Demo — seed dataset (E0 / GEN-2262).
 *
 * THROWAWAY DEMO CODE. Builds the initial `EmailIntelState` snapshot. Everything
 * is hardcoded and deterministic. The seed defaults to COLD OPEN: the corpus is
 * NOT yet shown (`corpus.voiceProfileExtracted === false`, `emailSent === false`,
 * `reply.received === false`, no recommendation `applied`). Scene 0 reveals the
 * corpus; the checkpoint helpers in `checkpoints.ts` advance state from here.
 *
 * Ids are namespaced `intel-*` so they never collide with the Vision-demo `demo-*`
 * fixtures already in the store.
 */

import type {
  EmailIntelCommsProfile,
  EmailIntelDraftSet,
  EmailIntelFact,
  EmailIntelRecommendation,
  EmailIntelState,
} from './email-intel.types';

/** Single source of truth for the email-intel cast names. */
export const INTEL_BRAND = {
  orgName: 'Pillar, Inc',
  collectorName: 'Sarah Chen',
  collectorEmail: 'sarah.chen@pillar-inc.com',
  crestlineName: 'Crestline Markets',
  granitePeakName: 'Granite Peak Distributors',
  cascadeName: 'Cascade Manufacturing Co.',
} as const;

/** Stable, namespaced ids (demo only; need not be real DB ids). */
const INTEL_ID = {
  org: 'intel-org-pillar',
  collector: 'intel-user-sarah-chen',
  crestlineCustomer: 'intel-cust-crestline-0001',
  granitePeakCustomer: 'intel-cust-granite-0002',
  cascadeCustomer: 'intel-cust-cascade-0003',
  crestlineThread: 'intel-thread-crestline-0001',
  granitePeakThread: 'intel-thread-granite-0001',
  cascadeThread: 'intel-thread-cascade-0001',
} as const;

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Per-customer comms profiles (Scene 0 customer list / Scene 4 sidebar)
// ---------------------------------------------------------------------------
const commsProfiles: EmailIntelCommsProfile[] = [
  {
    customerId: INTEL_ID.crestlineCustomer,
    customerName: INTEL_BRAND.crestlineName,
    preferredChannel: 'email',
    responseCadence: '~1 business day',
    sentiment: 'collaborative',
    summary:
      'Responsive and easy to work with. Replies within a business day and pays once the paperwork is clear.',
    keyContact: {
      name: 'Maria Alvarez',
      role: 'AP Specialist',
      email: 'maria.alvarez@crestlinemarkets.com',
    },
  },
  {
    customerId: INTEL_ID.granitePeakCustomer,
    customerName: INTEL_BRAND.granitePeakName,
    preferredChannel: 'email',
    responseCadence: '3–5 business days (slow, often after escalation)',
    sentiment: 'combative',
    summary:
      'Chronically disputes freight charges and short-pays. Payment is actually released by Maria, not the generic AP inbox.',
    keyContact: {
      name: 'Maria Okafor',
      role: 'AP Manager (releases payment)',
      email: 'm.okafor@granitepeak-dist.com',
    },
  },
  {
    customerId: INTEL_ID.cascadeCustomer,
    customerName: INTEL_BRAND.cascadeName,
    preferredChannel: 'email',
    responseCadence: '2–3 business days',
    sentiment: 'deferential',
    summary:
      'Loyal 10-year customer. Deferential and apologetic when late; responds well to a soft, relationship-first tone.',
    keyContact: {
      name: 'David Cho',
      role: 'Controller',
      email: 'david.cho@cascade-mfg.com',
    },
  },
];

// ---------------------------------------------------------------------------
// Draft variant sets (Scenes 2–3)
// ---------------------------------------------------------------------------

/** Crestline — generic-AI vs voice-matched overdue nudge (the HERO, Scene 2). */
const crestlineDrafts: EmailIntelDraftSet = {
  customerId: INTEL_ID.crestlineCustomer,
  customerName: INTEL_BRAND.crestlineName,
  scenario: 'Overdue-invoice nudge',
  variants: [
    {
      id: 'intel-draft-crestline-generic',
      kind: 'generic',
      label: 'Generic AI',
      subject: 'Past Due Invoice Reminder',
      body: [
        'Dear Valued Customer,',
        '',
        'This is a reminder that invoice INV-50231 in the amount of $24,180.00 is now past due. Please remit payment at your earliest convenience to avoid further action.',
        '',
        'If you have already sent payment, please disregard this notice.',
        '',
        'Regards,',
        'Accounts Receivable Department',
      ].join('\n'),
      rationale: [
        'Generic salutation — no relationship',
        'Formal, slightly threatening tone',
        'No knowledge of the customer or the contact',
      ],
    },
    {
      id: 'intel-draft-crestline-voice',
      kind: 'voice',
      label: 'Voice-matched',
      subject: 'Quick one on INV-50231',
      body: [
        'Hi Maria,',
        '',
        'Hope your week is off to a good start! Just a quick nudge — INV-50231 ($24,180) slipped a few days past due. No worries at all, I know things get busy on your end.',
        '',
        "You've always been great about turning these around quickly, so I figured a friendly heads-up was all you'd need. Let me know if you need me to resend anything.",
        '',
        'Best,',
        'Sarah',
      ].join('\n'),
      rationale: [
        "Opens on Maria's first name (real AP contact)",
        'References prior rapport — "always been great about turning these around"',
        'Warm but firm, short paragraphs',
        'Signs "Best, Sarah" — her real sign-off',
      ],
    },
  ],
};

/** Granite Peak — firm vs soft tone variants (Scene 3). */
const granitePeakDrafts: EmailIntelDraftSet = {
  customerId: INTEL_ID.granitePeakCustomer,
  customerName: INTEL_BRAND.granitePeakName,
  scenario: 'Freight short-pay follow-up',
  variants: [
    {
      id: 'intel-draft-granite-firm',
      kind: 'firm',
      label: 'Firm',
      subject: 'INV-49880 — balance due, POD attached',
      body: [
        'Hi Maria,',
        '',
        "I'm following up on INV-49880. The remittance came in short by $8,640 against the freight line. I've attached the signed POD — it confirms delivery in full, so the freight charge stands.",
        '',
        'Can you release the remaining balance this week? Happy to walk through the POD if anything looks off.',
        '',
        'Best,',
        'Sarah',
      ].join('\n'),
      rationale: [
        'Direct ask with a deadline',
        'Leads with evidence (POD) to pre-empt the freight dispute',
        'Addressed to Maria — who actually releases payment',
      ],
    },
    {
      id: 'intel-draft-granite-soft',
      kind: 'soft',
      label: 'Soft',
      subject: 'Quick question on INV-49880 freight',
      body: [
        'Hi Maria,',
        '',
        "Hope you're doing well! I noticed the payment on INV-49880 came in a little light on the freight line — wanted to check in before assuming anything.",
        '',
        "I've got the signed POD on hand if it'd help clear things up. Just let me know what works on your end and we'll get it squared away.",
        '',
        'Best,',
        'Sarah',
      ].join('\n'),
      rationale: [
        'Softer, question-led opener',
        'Still names the POD as available evidence',
        'Lower-pressure close for a combative account',
      ],
    },
  ],
};

/** Cascade — firm vs soft tone variants (Scene 3). */
const cascadeDrafts: EmailIntelDraftSet = {
  customerId: INTEL_ID.cascadeCustomer,
  customerName: INTEL_BRAND.cascadeName,
  scenario: 'Gentle overdue reminder (loyal customer)',
  variants: [
    {
      id: 'intel-draft-cascade-soft',
      kind: 'soft',
      label: 'Soft',
      subject: 'No rush — INV-50104 whenever you get a chance',
      body: [
        'Hi David,',
        '',
        "Hope all is well on your side! Totally minor — INV-50104 ($16,920) is a touch past due. Given how long we've worked together I know it's just a timing thing.",
        '',
        'Whenever it fits your cycle is fine. Let me know if you need anything from me.',
        '',
        'Best,',
        'Sarah',
      ].join('\n'),
      rationale: [
        'Relationship-first — references the 10-year history',
        'Low pressure, matches their deferential style',
        'Addressed to David Cho, their controller',
      ],
    },
    {
      id: 'intel-draft-cascade-firm',
      kind: 'firm',
      label: 'Firm',
      subject: 'INV-50104 now past due — please remit',
      body: [
        'Hi David,',
        '',
        'Following up on INV-50104 ($16,920), which is now past due. Could you let me know when we can expect payment?',
        '',
        'Appreciate you getting this on the schedule.',
        '',
        'Best,',
        'Sarah',
      ].join('\n'),
      rationale: [
        'More direct ask for a date',
        'Kept courteous given the loyal relationship',
        'Shown mainly to contrast with the recommended soft variant',
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Learned customer-intelligence facts with provenance (Scene 4 — Granite Peak)
// ---------------------------------------------------------------------------
const granitePeakFacts: EmailIntelFact[] = [
  {
    id: 'intel-fact-granite-contact',
    customerId: INTEL_ID.granitePeakCustomer,
    kind: 'best_contact',
    label: 'Maria releases payment — not the AP inbox',
    detail:
      'The generic AP inbox auto-replies, but Maria Okafor is the one who actually approves and releases checks. CC her directly.',
    provenance: {
      sourceThreadId: INTEL_ID.granitePeakThread,
      quote:
        "Please send all payment approvals to me directly — the AP inbox isn't monitored daily. — Maria",
      occurredAt: isoDaysAgo(118),
      author: 'Maria Okafor',
    },
  },
  {
    id: 'intel-fact-granite-latency',
    customerId: INTEL_ID.granitePeakCustomer,
    kind: 'response_latency',
    label: 'Typically replies in 3–5 business days',
    detail:
      'Across 14 threads, Granite Peak averages a 4-day reply and usually only after a second nudge.',
    provenance: {
      sourceThreadId: INTEL_ID.granitePeakThread,
      quote:
        'Sorry for the delay getting back to you — we were closing the quarter.',
      occurredAt: isoDaysAgo(64),
      author: 'Maria Okafor',
    },
  },
  {
    id: 'intel-fact-granite-dispute',
    customerId: INTEL_ID.granitePeakCustomer,
    kind: 'recurring_dispute',
    label: 'Always disputes freight charges',
    detail:
      'Freight is short-paid on roughly 3 of every 4 invoices. Attaching a signed POD up front resolves it fastest.',
    provenance: {
      sourceThreadId: INTEL_ID.granitePeakThread,
      quote:
        "We won't be paying the freight line until we see proof of delivery — same as last time.",
      occurredAt: isoDaysAgo(41),
      author: 'Maria Okafor',
    },
  },
  {
    id: 'intel-fact-granite-promise',
    customerId: INTEL_ID.granitePeakCustomer,
    kind: 'promise_to_pay',
    label: 'Honors promises once POD is provided',
    detail:
      'When a POD is attached, Granite Peak has paid the balance within a week in every prior instance.',
    provenance: {
      sourceThreadId: INTEL_ID.granitePeakThread,
      quote:
        "Got the POD, thanks. We'll release the balance on Friday's check run.",
      occurredAt: isoDaysAgo(28),
      author: 'Maria Okafor',
    },
  },
];

// ---------------------------------------------------------------------------
// Recommendations (Scene 5 — Granite Peak)
// ---------------------------------------------------------------------------
const recommendations: EmailIntelRecommendation[] = [
  {
    id: 'intel-rec-granite-pod',
    customerId: INTEL_ID.granitePeakCustomer,
    title: 'Pre-empt the freight dispute — attach the signed POD',
    rationale:
      'Granite Peak short-pays freight on ~75% of invoices. Leading with the POD has cleared it within a week every time.',
    applied: false,
  },
  {
    id: 'intel-rec-granite-cc-maria',
    customerId: INTEL_ID.granitePeakCustomer,
    title: 'CC Maria directly — she releases payment, not the AP inbox',
    rationale:
      'The AP inbox is unmonitored. Maria Okafor approves the checks; addressing her cuts the reply cycle in half.',
    applied: false,
  },
];

/** Build a fresh, fully-independent COLD-OPEN email-intel slice. */
export function buildEmailIntelSeed(): EmailIntelState {
  const state: EmailIntelState = {
    cast: {
      org: { id: INTEL_ID.org, name: INTEL_BRAND.orgName },
      collector: {
        id: INTEL_ID.collector,
        name: INTEL_BRAND.collectorName,
        email: INTEL_BRAND.collectorEmail,
      },
      crestline: {
        customerId: INTEL_ID.crestlineCustomer,
        customerName: INTEL_BRAND.crestlineName,
        threadId: INTEL_ID.crestlineThread,
      },
      granitePeak: {
        customerId: INTEL_ID.granitePeakCustomer,
        customerName: INTEL_BRAND.granitePeakName,
        threadId: INTEL_ID.granitePeakThread,
      },
      cascade: {
        customerId: INTEL_ID.cascadeCustomer,
        customerName: INTEL_BRAND.cascadeName,
        threadId: INTEL_ID.cascadeThread,
      },
    },
    // COLD OPEN: corpus not yet revealed.
    corpus: {
      // ~6 years of mail back to 2020: ~82k messages across ~9.6k threads
      // (~8.5 messages/thread), 87 distinct customers. Deliberately not round —
      // these read as a real measured count. Size is intentionally omitted (no
      // believable way to "download 100s of GB" in the load animation).
      threadsScanned: 9_643,
      customersFound: 87,
      yearsSpanStart: 2020,
      yearsSpanEnd: new Date().getFullYear(),
      messagesScanned: 82_497,
      voiceProfileExtracted: false,
    },
    voiceProfile: {
      collectorId: INTEL_ID.collector,
      collectorName: INTEL_BRAND.collectorName,
      signature: 'Best, Sarah',
      traits: [
        'Warm but firm',
        'Opens on first names',
        'Short paragraphs',
        'Leads with rapport before the ask',
        'Low-pressure, assumes good faith',
      ],
      characteristicPhrases: [
        {
          text: 'Just a quick nudge —',
          context: 'How she opens a gentle overdue reminder',
        },
        {
          text: 'No worries at all, I know things get busy on your end.',
          context: 'Softens the ask while staying on-message',
        },
        {
          text: 'Let me know if you need me to resend anything.',
          context: 'Her standard helpful close',
        },
      ],
    },
    commsProfiles,
    drafts: {
      crestline: crestlineDrafts,
      granitePeak: granitePeakDrafts,
      cascade: cascadeDrafts,
    },
    facts: granitePeakFacts,
    recommendations,
    reply: {
      customerId: INTEL_ID.granitePeakCustomer,
      customerName: INTEL_BRAND.granitePeakName,
      from: 'Maria Okafor',
      fromEmail: 'm.okafor@granitepeak-dist.com',
      subject: 'Re: INV-49880 — balance due, POD attached',
      body: [
        'Hi Sarah,',
        '',
        "Thanks for sending the POD over so quickly — that clears it up. I'll get the remaining $8,640 onto Friday's check run.",
        '',
        'Appreciate you making this easy.',
        '',
        'Maria',
      ].join('\n'),
      receivedAt: new Date().toISOString(),
      // COLD OPEN: reply has not arrived yet.
      received: false,
    },
    // COLD OPEN: email not yet sent.
    emailSent: false,
  };

  // Deep clone so callers can mutate freely without touching module state.
  return structuredClone(state);
}
